import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy,
} from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { NzMessageService } from "ng-zorro-antd/message";
import { NzModalService } from "ng-zorro-antd/modal";
import { AppsService } from "src/app/shared/services/apps.service";
import { PermissionService } from "src/app/services/permission.service";
import { isValidImei, IMEI_MESSAGE } from "src/app/shared/utils/imei.util";

/**
 * "Add New Device" / "Add New Vehicle" drawers.
 *
 * Pulled out of add-vechile.component so the same forms can be opened from any
 * page (Add Vehicle screen, Manage Vehicle dashboard, ...) without copying the
 * master-data lookups, the IMEI re-assignment flow and the save calls.
 *
 * Hosts open it through the template ref:
 *   <app-add-vehicle-device #frms [clntId]="c" [tntId]="t" (saved)="reload()">
 *   frms.openVehicle('new')  |  frms.openVehicle('Edit', row)  |  frms.openDevice()
 */
@Component({
  selector: "app-add-vehicle-device",
  templateUrl: "./add-vehicle-device.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AddVehicleDeviceComponent implements OnInit, OnChanges {
  /** Client the host page is working with. Falls back to the logged-in user's client. */
  @Input() clntId: any;
  /** Tenant/ULB the host page is working with. 0 ("All") is ignored - drawers need one tenant. */
  @Input() tntId: any;

  /** Fired after a vehicle or device is saved/edited so the host can refresh its list. */
  @Output() saved = new EventEmitter<"vehicle" | "device">();
  /** Fired whenever master data (IMEIs, categories, groups...) is reloaded. */
  @Output() masterDataLoaded = new EventEmitter<any>();
  /** Fired when the client/tenant is changed from inside a drawer. */
  @Output() contextChange = new EventEmitter<{ clnt_id: any; tnt_id: any }>();

  clnt_dtls: any = [];
  clnts: any;
  tnts: any = [];
  selectedclnt: any;
  selectedTnt: any;

  addVchleFrm = false;
  addDvceFrm = false;
  editform = false;
  sdbarHdr: string;
  // True while a save/edit request is in flight - disables the submit button
  // and prevents duplicate submissions on double-click or rapid taps.
  isSaving = false;
  addVhcldtlsform: FormGroup;

  mstrDta: any;
  dvceTypeLst: any;
  dvceCtgryLst: any;

  // Add-device drawer fields
  dvce_nm: any;
  dvce_type_id: any;
  dvce_ctgry_id: any;
  dvcimei_nu: any;
  mble_nu: any;
  sim_nu: any;

  // Vehicle edit state
  asrt_id: any;
  asrt_ctgry_nm: any;
  asrt_grp_nm: any;
  crnt_dvce_id: any;
  dvcImei: any;
  edtUlb: any;
  checked = false;
  igntn_in = 0;

  // IMEI already-in-use banner
  usdImei = false;
  tnt_nm: any;
  asrt_nm: any;
  unAsgnVeh: any;
  tenant: any;
  cndtn: boolean;

  /** ngOnChanges fires before ngOnInit - don't fetch master data twice on startup. */
  private initialized = false;

  constructor(
    public apiServ: AppsService,
    private modal: NzModalService,
    private message: NzMessageService,
    public perms: PermissionService
  ) {
    this.clnt_dtls = JSON.parse(localStorage.getItem("wto_clients")) || [];
    const userDtls = JSON.parse(localStorage.getItem("wto_w_user"));

    // Default client/tenant from the logged-in user; hosts can override via inputs.
    if (userDtls && userDtls.clnt_id) {
      this.selectedclnt = userDtls.clnt_id;
      const clientData = this.clnt_dtls.find(
        (c) => c.clnt_id === userDtls.clnt_id
      );
      if (clientData && clientData.tnts) {
        this.tnts = clientData.tnts;
        this.selectedTnt = userDtls.tnt_id
          ? userDtls.tnt_id
          : this.tnts.length > 0
          ? this.tnts[0].tnt_id
          : null;
      } else if (this.clnt_dtls.length > 0) {
        this.selectedclnt = this.clnt_dtls[0].clnt_id;
        this.tnts = this.clnt_dtls[0].tnts || [];
        this.selectedTnt = this.tnts.length > 0 ? this.tnts[0].tnt_id : null;
      }
    } else if (this.clnt_dtls.length > 0) {
      this.selectedclnt = this.clnt_dtls[0].clnt_id;
      this.tnts = this.clnt_dtls[0].tnts || [];
      this.selectedTnt = this.tnts.length > 0 ? this.tnts[0].tnt_id : null;
    }
  }

  ngOnInit(): void {
    this.getCLnt();
    this.getdvcetypeCtgry();
    this.addVhcldtlsform = new FormGroup({
      clnt: new FormControl(this.selectedclnt, [Validators.required]),
      tnt: new FormControl(this.selectedTnt, [Validators.required]),
      veh_nu: new FormControl("", [Validators.required]),
      imei_nu: new FormControl("", [Validators.required]),
      veh_ctgry: new FormControl("", [Validators.required]),
      veh_grp: new FormControl("", [Validators.required]),
      veh_sts: new FormControl("", [Validators.required]),
      icn_pth: new FormControl("", [Validators.required]),
      veh_typ: new FormControl("", [Validators.required]),
      hme_fnce: new FormControl(""),
      ULB: new FormControl(""),
      // Tracking type and limits
      blng_prmtr_id: new FormControl("1", [Validators.required]), // Default to distance (1)
      min_dst_ct: new FormControl(0), // Optional, defaults to 0
      max_spd_lmt_ct: new FormControl(0), // Optional, defaults to 0
      asrt_ownr_id: new FormControl("1", [Validators.required]), // 1=Own, 2=Hired
    });

    // The vehicle drawer edits client/tenant through its own form controls -
    // mirror those onto the fields the device drawer and the API calls use.
    this.addVhcldtlsform.get("clnt").valueChanges.subscribe((v) => {
      if (v && v !== this.selectedclnt) {
        this.setClient(v, true);
      }
    });
    this.addVhcldtlsform.get("tnt").valueChanges.subscribe((v) => {
      if (v && v !== this.selectedTnt) {
        this.slct_tnt(v);
      }
    });

    this.initialized = true;
    if (this.hasContext) {
      this.getMstrDta();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Keep the drawers pointed at whatever the host page is showing, but never
    // yank the selection out from under a form the user already has open.
    if (this.addVchleFrm || this.addDvceFrm) {
      return;
    }
    let ctxChanged = false;
    if (changes["clntId"] && this.clntId && this.clntId !== this.selectedclnt) {
      this.setClient(this.clntId, false);
      ctxChanged = true;
    }
    // tnt_id 0 means "All ULBs" on the dashboard filter - not a valid target.
    if (changes["tntId"] && this.tntId && this.tntId !== this.selectedTnt) {
      this.selectedTnt = this.tntId;
      this.addVhcldtlsform?.get("tnt").setValue(this.selectedTnt);
      ctxChanged = true;
    }
    // ngOnInit does the first load; afterwards follow the host's selection.
    if (ctxChanged && this.initialized && this.hasContext) {
      this.getMstrDta();
    }
  }

  /** True when a client + tenant are known, i.e. the drawers can be opened. */
  get hasContext(): boolean {
    return !!this.selectedclnt && !!this.selectedTnt;
  }

  // ------------------------------------------------------------------ //
  // Public API for host pages
  // ------------------------------------------------------------------ //

  /** Open the vehicle drawer. `mode` is 'new' or 'Edit' (row required for edit). */
  openVehicle(mode: "new" | "Edit", row?: any): void {
    if (!this.hasContext) {
      this.createMessage("error", "Please Select Client and Tenant");
      return;
    }
    this.edtUlb = null;
    this.getMstrDta();
    if (mode === "new") {
      this.editform = false;
      this.sdbarHdr = "Add New Vehicle";
      this.addVchleFrm = true;
      this.checked = false;
      this.igntn_in = 0;
      this.dvcImei = null;
      this.crnt_dvce_id = null;
      this.addVhcldtlsform.get("clnt").setValue(this.selectedclnt);
      this.addVhcldtlsform.get("tnt").setValue(this.selectedTnt);
      this.addVhcldtlsform.get("veh_nu").setValue("");
      this.addVhcldtlsform.get("imei_nu").setValue("");
      this.addVhcldtlsform.get("veh_ctgry").setValue("");
      this.addVhcldtlsform.get("veh_grp").setValue("");
      this.addVhcldtlsform.get("veh_sts").setValue("");
      this.addVhcldtlsform.get("icn_pth").setValue("");
      this.addVhcldtlsform.get("veh_typ").setValue("");
      this.addVhcldtlsform.get("hme_fnce").setValue("");
      this.addVhcldtlsform.get("blng_prmtr_id").setValue("1");
      this.addVhcldtlsform.get("min_dst_ct").setValue(1);
      this.addVhcldtlsform.get("max_spd_lmt_ct").setValue(40);
      this.addVhcldtlsform.get("asrt_ownr_id").setValue("1"); // Default to Own
    } else if (mode === "Edit" && row) {
      this.checked = row.igntn_cnct_in == 1 ? true : false;
      this.igntn_in = row.igntn_cnct_in == 1 ? 1 : 0;
      this.sdbarHdr = "Edit Vehicle";
      this.addVchleFrm = true;
      this.editform = true;
      this.asrt_id = row.asrt_id;
      this.asrt_ctgry_nm = row.asrt_ctgry_nm;
      this.asrt_grp_nm = row.asrt_grp_nm;
      this.dvcImei = row.dev_imei_nu;
      this.addVhcldtlsform.get("clnt").setValue(this.selectedclnt);
      this.addVhcldtlsform.get("tnt").setValue(this.selectedTnt);
      this.addVhcldtlsform.get("veh_nu").setValue(row.asrt_nm);
      this.addVhcldtlsform.get("imei_nu").setValue(row.dvce_id);
      this.addVhcldtlsform.get("veh_ctgry").setValue(row.asrt_ctgry_id);
      this.addVhcldtlsform.get("veh_grp").setValue(row.asrt_grp_id);
      this.addVhcldtlsform.get("veh_sts").setValue(row.asrt_sts_id);
      this.addVhcldtlsform.get("ULB").setValue(row.tnt_id);
      this.addVhcldtlsform.get("icn_pth").setValue(row.icon_path);
      this.addVhcldtlsform.get("hme_fnce").setValue(row.hme_fnce_id);
      this.addVhcldtlsform.get("veh_typ").setValue(`${row.clap_in}`);
      this.addVhcldtlsform
        .get("blng_prmtr_id")
        .setValue(row.blng_prmtr_id ? String(row.blng_prmtr_id) : "1");
      this.addVhcldtlsform.get("min_dst_ct").setValue(row.min_dst_ct || 1);
      this.addVhcldtlsform
        .get("max_spd_lmt_ct")
        .setValue(row.max_spd_lmt_ct || 40);
      this.addVhcldtlsform
        .get("asrt_ownr_id")
        .setValue(row.asrt_ownr_id ? String(row.asrt_ownr_id) : "1");
    }
  }

  /** Open the device drawer. */
  openDevice(): void {
    if (!this.hasContext) {
      this.createMessage("error", "Please Select Client and Tenant");
      return;
    }
    this.addDvceFrm = true;
  }

  /** Reload master data for the current client/tenant. */
  loadMasterData(): void {
    if (this.hasContext) {
      this.getMstrDta();
    }
  }

  // ------------------------------------------------------------------ //
  // Internals
  // ------------------------------------------------------------------ //

  getCLnt() {
    this.apiServ.get(`drivers/add/clntdtls`).subscribe(
      (res) => {
        if (res["status"] == 200) {
          this.clnts = res["data"];
        }
      },
      (err) => {
        console.log(err);
      }
    );
  }

  getdvcetypeCtgry() {
    this.apiServ.get(`drivers/vehicle/device/types/categories/list`).subscribe(
      (res) => {
        if (res["status"] === 200) {
          this.dvceTypeLst = res["data"].dvceTypelst;
          this.dvceCtgryLst = res["data"].dvceCtgrylst;
        }
      },
      (err) => {}
    );
  }

  getMstrDta() {
    this.apiServ
      .get(
        `drivers/add/masterData/${this.selectedclnt}/${
          this.edtUlb ? this.edtUlb : this.selectedTnt
        }`
      )
      .subscribe(
        (res) => {
          if (res["status"] == 200) {
            this.mstrDta = res["data"];
            this.masterDataLoaded.emit(this.mstrDta);
          }
        },
        (err) => {
          console.log(err);
        }
      );
  }

  /** Client changed from inside a drawer - refresh its tenant list. */
  slct_clnt(value) {
    this.setClient(value, true);
  }

  private setClient(value, notify: boolean) {
    this.selectedclnt = value;
    const clientData = this.clnt_dtls.find((c) => c.clnt_id === value);
    this.tnts = clientData && clientData.tnts ? clientData.tnts : [];
    // Keep the tenant only when it still belongs to the newly selected client.
    if (!this.tnts.some((t) => t.tnt_id === this.selectedTnt)) {
      this.selectedTnt = null;
    }
    this.addVhcldtlsform?.get("clnt").setValue(this.selectedclnt);
    this.addVhcldtlsform?.get("tnt").setValue(this.selectedTnt);
    if (notify) {
      this.contextChange.emit({
        clnt_id: this.selectedclnt,
        tnt_id: this.selectedTnt,
      });
    }
  }

  /** Tenant changed from inside a drawer. */
  slct_tnt(val) {
    this.selectedTnt = val;
    this.addVhcldtlsform?.get("tnt").setValue(val);
    if (this.hasContext) {
      this.getMstrDta();
    }
    this.contextChange.emit({
      clnt_id: this.selectedclnt,
      tnt_id: this.selectedTnt,
    });
  }

  close() {
    this.addVchleFrm = false;
    this.usdImei = false;
  }

  closedvcFrm() {
    this.addDvceFrm = false;
  }

  addVechile() {
    // Guard: ignore extra clicks while a previous submission is still pending.
    if (this.isSaving) {
      return;
    }
    // Frontend permission gate (backend enforces it too).
    if (!this.perms.canWrite()) {
      this.createMessage(
        "error",
        "You do not have permission to save vehicles."
      );
      return;
    }
    // 2026-08-31 duplicate report: two vehicles of the same ULB shown against
    // one IMEI in the Manage Vehicle grid.
    //
    // The amber banner above has always said the IMEI is taken and offered to
    // unassign the holder, but nothing made the user click it - so ignoring it
    // and pressing Save bound a second vehicle to the same tracker. That is how
    // 205 IMEIs came to be held by more than one active vehicle.
    //
    // THE API IS THE CONTROL: deviceGuard's exclusivity check refuses this on
    // all four vehicle-write endpoints and answers 400. This check only moves
    // the message into the drawer, where the "Click Here" link that fixes it is.
    //
    // It applies to the EDIT branch too, unlike the IMEI format check below: on
    // an edit `usdImei` now means ANOTHER vehicle holds it (ImeiSlct filters
    // this vehicle out), and the server refuses that case on the update paths
    // as well - only resubmitting the vehicle's OWN stored IMEI is grandfathered.
    if (this.usdImei) {
      this.createMessage(
        "error",
        `IMEI ${this.dvcImei} is already assigned to ${this.asrt_nm}. Unassign it from that vehicle first.`
      );
      return;
    }
    if (this.addVhcldtlsform.valid) {
      this.isSaving = true;
      if (this.editform) {
        const postData = {
          asrt_id: this.asrt_id,
          asrt_ctgry_nm: this.asrt_ctgry_nm,
          asrt_grp_nm: this.asrt_grp_nm,
          clnt_id: this.addVhcldtlsform.value.clnt,
          tnt_id: this.addVhcldtlsform.value.tnt,
          asrt_nm: this.addVhcldtlsform.value.veh_nu,
          clap_in: this.addVhcldtlsform.value.veh_typ,
          dev_imei_nu: this.dvcImei,
          crnt_dvce_id: this.addVhcldtlsform.value.imei_nu,
          igntn_cnct_in: this.igntn_in,
          asrt_ctgry_id: this.addVhcldtlsform.value.veh_ctgry,
          asrt_grp_id: this.addVhcldtlsform.value.veh_grp,
          asrt_sts_id: this.addVhcldtlsform.value.veh_sts,
          icon_path: this.addVhcldtlsform.value.icn_pth,
          hme_fnce_id: this.addVhcldtlsform.value.hme_fnce || 0,
          edit_ulb: this.edtUlb,
          blng_prmtr_id: Number(this.addVhcldtlsform.value.blng_prmtr_id) || 1,
          min_dst_ct: Number(this.addVhcldtlsform.value.min_dst_ct) || 0,
          max_spd_lmt_ct:
            Number(this.addVhcldtlsform.value.max_spd_lmt_ct) || 0,
          asrt_ownr_id: Number(this.addVhcldtlsform.value.asrt_ownr_id) || 1,
        };
        this.apiServ.post(postData, `drivers/Update/vechile/data`).subscribe(
          (res) => {
            this.isSaving = false;
            if (res["status"] == 200) {
              this.createMessage("success", "Vehicle successfully Edited");
            }
            this.saved.emit("vehicle");
            this.close();
          },
          (err) => {
            this.isSaving = false;
            this.createMessage("error", `${err}`);
            console.log(err);
          }
        );
      } else {
        // Audit obs #4 re-test (CWE-840): this drawer is the auditors' PoC screen.
        // The IMEI dropdown is filled from the device master, which still holds 13
        // malformed rows left by the original hole ('222', '369', six stored as
        // '0'), so an invalid IMEI is a SELECTABLE OPTION here - the value never
        // had to be tampered with in a proxy.
        //
        // THE API IS THE CONTROL. deviceGuard.assertImeiFormatOnCreate refuses this
        // unconditionally on POST /drivers/add/NewVechileToDB and answers 400. This
        // check only moves the message into the drawer, where the user can act on it.
        //
        // DELIBERATELY NOT IN THE EDIT BRANCH ABOVE. The server stages the same rule
        // on the update endpoints ('imeiFormatBind') because 31 ACTIVE vehicles are
        // already bound to a malformed IMEI and blocking their edits strands them.
        // Adding it there would refuse in the browser what the API allows.
        if (!isValidImei(this.dvcImei)) {
          this.isSaving = false;
          this.createMessage("error", IMEI_MESSAGE);
          return;
        }
        const data = {
          crnt_dvce_id: this.crnt_dvce_id,
          asrt_ctgry_nm: this.asrt_ctgry_nm,
          asrt_grp_nm: this.asrt_grp_nm,
          clnt_id: this.addVhcldtlsform.value.clnt,
          tnt_id: this.addVhcldtlsform.value.tnt,
          asrt_nm: this.addVhcldtlsform.value.veh_nu,
          clap_in: this.addVhcldtlsform.value.veh_typ,
          dev_imei_nu: this.dvcImei,
          igntn_cnct_in: this.igntn_in,
          asrt_ctgry_id: this.addVhcldtlsform.value.veh_ctgry,
          asrt_grp_id: this.addVhcldtlsform.value.veh_grp,
          asrt_sts_id: this.addVhcldtlsform.value.veh_sts,
          icon_path: this.addVhcldtlsform.value.icn_pth,
          hme_fnce_id: this.addVhcldtlsform.value.hme_fnce || 0,
          blng_prmtr_id: Number(this.addVhcldtlsform.value.blng_prmtr_id) || 1,
          min_dst_ct: Number(this.addVhcldtlsform.value.min_dst_ct) || 0,
          max_spd_lmt_ct:
            Number(this.addVhcldtlsform.value.max_spd_lmt_ct) || 0,
          asrt_ownr_id: Number(this.addVhcldtlsform.value.asrt_ownr_id) || 1,
        };
        this.apiServ.post(data, `drivers/add/NewVechileToDB`).subscribe(
          (res) => {
            this.isSaving = false;
            if (res["status"] == 200) {
              this.createMessage("success", "Vehicle successfully Added");
              this.close();
              this.saved.emit("vehicle");
            }
          },
          (err) => {
            this.isSaving = false;
            // A guard rejection arrives as 400 with a message written to be shown
            // (see deviceGuard's contract - it leaks no internals). Interpolating the
            // HttpErrorResponse itself printed "Http failure response for ...", which
            // tells the user nothing about which field the server refused.
            this.createMessage(
              "error",
              err?.error?.message ||
                "Failed to add the vehicle. Please try again."
            );
            console.log(err);
          }
        );
      }
    } else {
      this.createMessage("error", "Please enter all the mandatory fields");
    }
  }

  addDevice() {
    if (!this.perms.canWrite()) {
      this.createMessage("error", "You do not have permission to add devices.");
      return;
    }
    if (!this.selectedclnt) {
      this.createMessage("error", "Please select Client");
      return;
    }
    if (!this.selectedTnt) {
      this.createMessage("error", "Please select Tenant");
      return;
    }
    if (!this.dvce_type_id) {
      this.createMessage("error", "Please select Device Type");
      return;
    }
    if (!this.dvce_ctgry_id) {
      this.createMessage("error", "Please select Device Model");
      return;
    }
    if (!this.dvcimei_nu || this.dvcimei_nu.toString().trim() === "") {
      this.createMessage("error", "Please enter IMEI Number");
      return;
    }
    // Audit obs #4 (CWE-840): the API is the control here - it re-checks this
    // with the same rule and answers 400. This check only moves the message
    // into the drawer, where the user can act on it.
    //
    // Was `isNaN(v) || v.length < 10`, which is a numeric-parse test rather than
    // a digit test and let through '1e10000000', ' 12345678901 ' and decimals -
    // none of which the label's "minimum 10 digits" ever meant. Shared with
    // assign-device.component so the two screens posting to this endpoint cannot
    // disagree again.
    if (!isValidImei(this.dvcimei_nu)) {
      this.createMessage("error", IMEI_MESSAGE);
      return;
    }

    const postData = {
      dvce_nm: this.dvce_nm,
      dvce_type_id: this.dvce_type_id,
      dvce_ctgry_id: this.dvce_ctgry_id,
      imei_nu: this.dvcimei_nu,
      mble_nu: this.mble_nu,
      clnt_id: this.selectedclnt,
      tnt_id: this.selectedTnt,
      sim_nu: this.sim_nu,
    };
    this.apiServ
      .post(postData, `drivers/vehicles/Master/Device/update`)
      .subscribe(
        (res) => {
          if (res["status"] == 200) {
            this.createMessage("success", "Device Added Sucessfully");
            this.getMstrDta();
            this.closedvcFrm();
            this.dvce_nm = "";
            this.dvce_type_id = null;
            this.dvce_ctgry_id = null;
            this.dvcimei_nu = "";
            this.mble_nu = "";
            this.sim_nu = "";
            this.saved.emit("device");
          }
        },
        (err) => {
          console.log(err);
          this.createMessage(
            "error",
            err.error?.message || "Failed to add device. Please try again."
          );
        }
      );
  }

  ctgrtSlct(e) {
    this.mstrDta.ctgry.forEach((val) => {
      if (val.asrt_ctgry_id == e) {
        this.asrt_ctgry_nm = val.asrt_ctgry_nm;
      }
    });
  }

  grpSlct(grp) {
    this.mstrDta.Groups.forEach((val) => {
      if (val.asrt_grp_id == grp) {
        this.asrt_grp_nm = val.asrt_grp_nm;
      }
    });
  }

  ImeiSlct(im) {
    this.cndtn = false;
    this.mstrDta.Imei.forEach((val) => {
      if (val.dvce_id == im) {
        this.dvcImei = val.imei_nu;
        this.crnt_dvce_id = im;
      }
    });
    this.apiServ.get(`drivers/check/Imei/${this.dvcImei}`).subscribe((res) => {
      // The vehicle being EDITED is not "another vehicle". check/Imei answers
      // "which active vehicles hold this IMEI", and in the edit branch that
      // includes this one - so without the filter, reopening a vehicle and
      // saving it unchanged would raise the duplicate banner against itself and
      // (since 2026-08-31) block its own save. The server grandfathers exactly
      // the same case through deviceGuard.assertImeiExclusive.
      const holders = (res["data"] || []).filter(
        (v) => !this.editform || String(v.asrt_id) !== String(this.asrt_id)
      );
      if (holders.length == 0) {
        this.usdImei = false;
      } else {
        this.tenant = holders[0].tnt_id;
        this.tnt_nm = holders[0].tnt_nm;
        this.asrt_nm = holders[0].asrt_nm;
        this.unAsgnVeh = holders[0].asrt_id;
        this.usdImei = true;
        this.tnts.forEach((val) => {
          if (val.tnt_id == this.tenant) {
            this.cndtn = true;
          }
        });
      }
    });
  }

  ign_ckd(e) {
    this.igntn_in = e ? 1 : 0;
  }

  UnasgnDvce() {
    const postdata = {
      asrt_id: this.unAsgnVeh,
    };
    this.apiServ.post(postdata, `drivers/Unassign/Device`).subscribe(
      (res) => {
        if (res["status"] == 200) {
          this.createMessage("success", "Device Unassigned Sucessfully");
          this.addVhcldtlsform.get("imei_nu").setValue("");
          this.usdImei = false;
        }
      },
      (err) => {
        console.log(err);
      }
    );
  }

  cnfrmDvcUnAssgn(): void {
    this.modal.confirm({
      nzTitle: "Are you sure unassign  this device?",
      nzContent: '<b style="color: red;"></b>',
      nzOkText: "Yes",
      nzOkType: "primary",
      nzOkDanger: true,
      nzOnOk: () => this.UnasgnDvce(),
      nzCancelText: "No",
      nzOnCancel: () => console.log("Cancel"),
    });
  }

  editUlb(ulb) {
    this.edtUlb = ulb;
    this.addVhcldtlsform.get("veh_ctgry").setValue("");
    this.addVhcldtlsform.get("veh_grp").setValue("");
    this.getMstrDta();
  }

  createMessage(type: string, msg: string): void {
    this.message.create(type, msg);
  }
}
